/** Canvas HUD only. World rendering lives in engine3d/scene.ts. */
import { VIEW_W } from './constants'
import type { Camera } from '../core/camera'
import type { Level } from '../game/level'
import { drawText, textWidth } from './font'
import { num, toInt } from './visuals'

/** Short floating `+N` / `1UP` toast over the playfield. */
export interface HudPopup {
  x: number
  y: number
  text: string
  colour: string
  age: number
  life: number
}

const POPUP_LIFE = 45
const popups: HudPopup[] = []

/** Spawn a world-space toast; `x`/`y` are native playfield pixels. */
export function spawnHudPopup(
  x: number,
  y: number,
  text: string,
  colour = '#fcfcfc',
): void {
  // Mass pickups and headless playtests must not grow presentation state forever.
  if (popups.length >= 32) popups.shift()
  popups.push({
    x,
    y,
    text,
    colour,
    age: 0,
    life: POPUP_LIFE,
  })
}

/** Advance toast ages; call once per simulation step, not from render. */
export function tickHudPopups(): void {
  for (let i = popups.length - 1; i >= 0; i--) {
    const p = popups[i]
    if (p === undefined) continue
    p.age++
    if (p.age >= p.life) popups.splice(i, 1)
  }
}

/** Drop every toast (course load / leave). */
export function clearHudPopups(): void {
  popups.length = 0
}

/** Live toast count — for tests. */
export function hudPopupCount(): number {
  return popups.length
}

/** Snapshot of active toasts — for tests. */
export function hudPopups(): readonly HudPopup[] {
  return popups.map((popup) => ({ ...popup }))
}

/**
 * Draw floating score/event toasts in camera space (same transform as the HUD).
 */
export function drawHudPopups(
  ctx: CanvasRenderingContext2D,
  camera: Camera,
): void {
  if (popups.length === 0) return
  ctx.save()
  for (const p of popups) {
    const t = p.age / p.life
    const sx = Math.floor(p.x - camera.x - textWidth(p.text) / 2)
    const sy = Math.floor(p.y - camera.y - p.age * 0.7)
    ctx.globalAlpha = Math.max(0, 1 - t * t)
    drawText(ctx, p.text, sx, sy, p.colour)
  }
  ctx.restore()
}

const HUD_COLUMNS: { label: string; x: number }[] = [
  { label: 'TAVI', x: 8 },
  { label: 'SHARDS', x: 56 },
  { label: 'SCORE', x: 112 },
  { label: 'ZONE', x: 176 },
  { label: 'TIME', x: 232 },
]
const HUD_ROW_1 = 6
const HUD_ROW_2 = 15
/** The clock flashes below 100 seconds. */
const HURRY_THRESHOLD = 100

function pad(value: number, width: number): string {
  const n = Math.max(0, Math.floor(value))
  return String(n).padStart(width, '0')
}

export function drawHud(
  ctx: CanvasRenderingContext2D,
  level: Level,
  opts: { blink?: boolean; world?: string } = {},
): void {
  const stats = level.stats
  const secondsLeft = Math.max(0, Math.ceil(num(stats.time) ?? 0))
  const hurry = secondsLeft > 0 && secondsLeft < HURRY_THRESHOLD
  // Blink only when asked, so a paused game can hold the clock still.
  const flash =
    hurry && (opts.blink ?? true) && Math.floor(level.tick / 15) % 2 === 1
  const timeColour = flash ? '#f87858' : '#fcfcfc'

  const values: string[] = [
    `x${pad(toInt(stats.lives), 2)}`,
    `x${pad(toInt(stats.coins), 2)}`,
    pad(toInt(stats.score), 6),
    // Supplied by the shell, which knows where this course sits in the library.
    // It used to be the literal '1-1' on every course in the game, so the HUD
    // claimed you were replaying the first level of the first world no matter
    // where you actually were.
    opts.world ?? '1-1',
    String(secondsLeft),
  ]
  const colours: string[] = [
    '#fcfcfc',
    '#fcfcfc',
    '#fcfcfc',
    '#fcd800',
    timeColour,
  ]

  ctx.save()
  ctx.fillStyle = 'rgba(14, 25, 34, 0.88)'
  ctx.fillRect(0, 0, VIEW_W, 26)
  for (let i = 0; i < HUD_COLUMNS.length; i++) {
    const col = HUD_COLUMNS[i]
    if (col === undefined) continue
    const value = values[i]
    const colour = colours[i]
    if (value === undefined || colour === undefined) continue
    drawText(ctx, col.label, col.x, HUD_ROW_1, '#85ded1', { outline: null })
    drawText(ctx, value, col.x, HUD_ROW_2, colour, { outline: null })
  }
  ctx.restore()
}
